/**
 * Charts for the deck. Inline SVG, real numbers, no library.
 *
 * "Panels, Infographics and Charts, Live data" was the ask. No charting library is added
 * for it: the shapes needed here are a stacked bar and a labelled donut, both a handful
 * of path maths, and a library would add a dependency, a bundle cost and a second
 * theming system to keep in step with the tokens.
 *
 * Every value is passed in from the payload. A chart with nothing behind it renders as
 * an explicit empty state rather than an empty frame, because an axis with no data looks
 * like a loading bug.
 */

/** One slice or segment. `value` is absolute; proportions are computed here. */
export interface Slice {
  label: string;
  value: number;
  /** A token name, e.g. `--accent`. Kept as a token so charts follow the theme. */
  token: string;
}

const total = (slices: Slice[]): number => slices.reduce((n, s) => n + s.value, 0);

/**
 * A horizontal stacked bar with a legend.
 *
 * Used for the commitment lanes, where the POINT is proportion: six actionable against
 * 925 archived is the story, and six separate numbers hide it.
 */
export function StackedBar({ slices, caption }: { slices: Slice[]; caption?: string }): JSX.Element {
  const sum = total(slices);
  if (sum === 0) {
    return <p className="mo-empty">Nothing to chart yet.</p>;
  }
  return (
    <figure className="chart">
      <div className="chart-bar" role="img" aria-label={caption ?? 'Distribution'}>
        {slices
          .filter((s) => s.value > 0)
          .map((s) => (
            <span
              key={s.label}
              className="chart-seg"
              style={{
                // Percentage width, so the bar reflows with the panel rather than
                // needing a measured pixel width.
                width: `${(s.value / sum) * 100}%`,
                background: `var(${s.token})`,
              }}
              title={`${s.label}: ${s.value}`}
            />
          ))}
      </div>
      <ul className="chart-legend">
        {slices.map((s) => (
          <li key={s.label} className="chart-key" data-zero={s.value === 0}>
            <span className="chart-swatch" style={{ background: `var(${s.token})` }} />
            <span className="chart-key-label">{s.label}</span>
            <span className="chart-key-value">{s.value}</span>
          </li>
        ))}
      </ul>
      {caption !== undefined && <figcaption className="chart-caption">{caption}</figcaption>}
    </figure>
  );
}

/**
 * A donut with a total in the middle.
 *
 * Drawn with `stroke-dasharray` on circles rather than arc paths: fewer places to get
 * the maths wrong, and it animates by stroke offset without touching layout.
 */
export function Donut({
  slices,
  caption,
  centreLabel,
}: {
  slices: Slice[];
  caption?: string;
  centreLabel?: string;
}): JSX.Element {
  const sum = total(slices);
  if (sum === 0) return <p className="mo-empty">Nothing to chart yet.</p>;

  const R = 42;
  const C = 2 * Math.PI * R;
  let offset = 0;

  return (
    <figure className="chart">
      <div className="chart-donut-wrap">
        <svg className="chart-donut" viewBox="0 0 100 100" role="img" aria-label={caption ?? 'Breakdown'}>
          {slices
            .filter((s) => s.value > 0)
            .map((s) => {
              const len = (s.value / sum) * C;
              const el = (
                <circle
                  key={s.label}
                  className="chart-arc"
                  cx="50"
                  cy="50"
                  r={R}
                  stroke={`var(${s.token})`}
                  strokeDasharray={`${len} ${C - len}`}
                  strokeDashoffset={-offset}
                />
              );
              offset += len;
              return el;
            })}
        </svg>
        <div className="chart-donut-centre">
          <span className="chart-donut-total">{sum.toLocaleString()}</span>
          {centreLabel !== undefined && (
            <span className="chart-donut-label">{centreLabel}</span>
          )}
        </div>
      </div>
      <ul className="chart-legend">
        {slices
          .filter((s) => s.value > 0)
          .map((s) => (
            <li key={s.label} className="chart-key">
              <span className="chart-swatch" style={{ background: `var(${s.token})` }} />
              <span className="chart-key-label">{s.label}</span>
              <span className="chart-key-value">{Math.round((s.value / sum) * 100)}%</span>
            </li>
          ))}
      </ul>
      {caption !== undefined && <figcaption className="chart-caption">{caption}</figcaption>}
    </figure>
  );
}
